import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { LegalLayout, type LegalTocItem } from "@/components/legal/LegalLayout";
import { cookiesTranslations } from "@/lib/legal/cookiesTranslations";
import { isLandingLocale, localePath } from "@/lib/landing/locale";

export default function Cookies() {
  const { lang } = useParams<{ lang: string }>();
  // The language comes from the URL (as on the landing page), so the
  // prerendered HTML and the first client render agree.
  const locale = isLandingLocale(lang) ? lang : "cs";
  const t = cookiesTranslations[locale];
  const toc: LegalTocItem[] = [
    { id: "s1", label: t.s1Title },
    { id: "s2", label: t.s2Title },
    { id: "s3", label: t.s3Title },
    { id: "s4", label: t.s4Title },
    { id: "s5", label: t.s5Title },
    { id: "s6", label: t.s6Title },
    { id: "s7", label: t.s7Title },
    { id: "s8", label: t.s8Title },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalLayout
      locale={locale}
      path="/cookies"
      title={t.title}
      subtitle={t.subtitle}
      effective={t.effectiveDate}
      toc={toc}
    >
      <div className="space-y-12">
        <section id="s1">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s1Title}
          </h2>
          <p>{t.s1Body}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s2">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s2Title}
          </h2>
          <p>{t.s2p1}</p>
          <p className="mt-3">{t.s2p2}</p>
          <p className="mt-3">{t.s2p3}</p>
          <p className="mt-3 text-sm text-muted-foreground">{t.s2p4}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s3">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s3Title}
          </h2>
          <p className="mb-4">{t.s3Intro}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="pb-2 pr-4 text-left font-medium text-foreground">
                    {t.tableHeaders.type}
                  </th>
                  <th className="pb-2 pr-4 text-left font-medium text-foreground">
                    {t.tableHeaders.namePurpose}
                  </th>
                  <th className="pb-2 pr-4 text-left font-medium text-foreground">
                    {t.tableHeaders.retention}
                  </th>
                  <th className="pb-2 text-left font-medium text-foreground">
                    {t.tableHeaders.essential}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {t.tableRows.map((row) => (
                  <tr key={row.namePurpose}>
                    <td className="py-2 pr-4">{row.type}</td>
                    <td className="py-2 pr-4">{row.namePurpose}</td>
                    <td className="py-2 pr-4">{row.retention}</td>
                    <td className="py-2 text-[hsl(var(--action))] dark:text-primary">
                      {row.essential}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <hr className="border-border/70" />

        <section id="s4">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s4Title}
          </h2>
          <p>{t.s4Body}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s5">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s5Title}
          </h2>
          <p>{t.s5p1}</p>
          <p className="mt-3">{t.s5p2}</p>
          <ul className="mt-2 list-disc space-y-1.5 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
            <li>
              <strong className="font-medium text-foreground">Chrome:</strong> {t.browsers.chrome}
            </li>
            <li>
              <strong className="font-medium text-foreground">Firefox:</strong> {t.browsers.firefox}
            </li>
            <li>
              <strong className="font-medium text-foreground">Safari:</strong> {t.browsers.safari}
            </li>
            <li>
              <strong className="font-medium text-foreground">Edge:</strong> {t.browsers.edge}
            </li>
          </ul>
          <div className="mt-4 rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
            <strong className="font-medium">{t.s5WarnTitle}</strong> {t.s5WarnBody}
          </div>
        </section>

        <hr className="border-border/70" />

        <section id="s6">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s6Title}
          </h2>
          <p>{t.s6Body}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s7">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s7Title}
          </h2>
          <p>{t.s7p1}</p>
          <p className="mt-3">
            {t.s7p2Prefix}
            <Link
              to={localePath(locale, "/privacy")}
              className="font-medium text-[hsl(var(--action))] underline-offset-2 hover:underline dark:text-primary"
            >
              {t.privacyLinkLabel}
            </Link>
            .
          </p>
        </section>

        <hr className="border-border/70" />

        <section id="s8">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s8Title}
          </h2>
          <div className="rounded-xl border bg-secondary/40 px-5 py-4 text-sm">
            <p className="font-medium text-foreground">IDEx Corporate s.r.o.</p>
            <p className="mt-1 text-muted-foreground">IČO: 18002161</p>
            <p className="text-muted-foreground">Kurzova 2222/16, Stodůlky, 155 00 Praha 5</p>
            <p className="text-muted-foreground">
              {t.contactEmailLabel}{" "}
              <a
                href="mailto:info@idex-corp.com"
                className="font-medium text-[hsl(var(--action))] underline-offset-2 hover:underline dark:text-primary"
              >
                info@idex-corp.com
              </a>
            </p>
          </div>
          <div className="mt-6 text-sm text-muted-foreground">
            <p>{t.closingPlaceDate}</p>
            <p className="mt-1 font-medium text-foreground">IDEx Corporate s.r.o.</p>
          </div>
        </section>
      </div>
    </LegalLayout>
  );
}
